import { useAppSelector } from "@/store/store-hooks";
import { ContextualBanner } from "@faro-lotv/flat-ui";
import type { JSX, ReactElement } from "react";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { ReactComponent as EdgeDeleteSvg } from "../icons/registration-edge-delete.svg";
import { ReactComponent as EdgeAddSvg } from "../icons/registration-edge-new.svg";
import { ReactComponent as SelectionObjectSvg } from "../icons/selection-object.svg";
import { ReactComponent as TransformMoveSvg } from "../icons/transform-move.svg";
import { selectSelectedEntityIds } from "../store/data-preparation-ui/data-preparation-ui-selectors";
import { selectEditMode } from "../store/user-edits/data-preparation-user-edit-selectors";
import { RegistrationEditMode } from "../store/user-edits/user-edit-types";
import { Projection } from "./projection-switch";

enum ContextualBannerStatus {
  IsEditingWithNoEntitySelected = "Select a capture or cluster to reposition.",
  IsEntitySelected = "Move and rotate the selection to pre-position your data. Run a refinement to improve the result.",
  // no icon
  NothingSelectedDefaultState = "Choose a capture or cluster that you want to move and rotate.",
  IsConnectionAdding = "Choose capture markers to create a connection between them.",
  IsConnectionDeleting = "To remove a connection line, simply click on it.",
}

const statusIconMap: Record<ContextualBannerStatus, JSX.Element | undefined> = {
  [ContextualBannerStatus.IsEditingWithNoEntitySelected]: (
    <SelectionObjectSvg />
  ),
  [ContextualBannerStatus.IsEntitySelected]: <TransformMoveSvg />,
  [ContextualBannerStatus.NothingSelectedDefaultState]: undefined,
  [ContextualBannerStatus.IsConnectionAdding]: <EdgeAddSvg />,
  [ContextualBannerStatus.IsConnectionDeleting]: <EdgeDeleteSvg />,
};

/**
 * Renders the help banner for the data preparation page.
 *
 * @returns The contextual help banner element or null if not shown.
 */
export function DataPreparationHelpBanner(): ReactElement | null {
  const statusKey = useHelpMessageStatus();

  if (!statusKey) return null;

  return (
    <ContextualBanner
      icon={statusIconMap[statusKey]}
      sx={{
        paddingY: 0.65,
      }}
    >
      {statusKey}
    </ContextualBanner>
  );
}

/**
 * Returns the contextual banner status key to show to the user, or `undefined` if no help should be shown.
 *
 * @returns The status key for the contextual banner, or undefined if no banner should be shown.
 */
function useHelpMessageStatus(): ContextualBannerStatus | undefined {
  const editMode = useAppSelector(selectEditMode);
  const isEntitySelected = useAppSelector(selectSelectedEntityIds).length > 0;
  const { projection } = useDataPreparationOverlayElements();
  if (!editMode || projection === Projection.threeDimensional) return undefined;

  switch (editMode) {
    case RegistrationEditMode.editScans:
      return isEntitySelected
        ? ContextualBannerStatus.IsEntitySelected
        : ContextualBannerStatus.IsEditingWithNoEntitySelected;
    case RegistrationEditMode.addConnection:
      return ContextualBannerStatus.IsConnectionAdding;
    case RegistrationEditMode.deleteConnection:
      return ContextualBannerStatus.IsConnectionDeleting;
  }
}
